/* The Italian Chocolate 1971 · tic-album.js · "Album" home section (sections/tic-album.liquid). No dependencies.
   The section's inline script has already chosen the mode before first paint (data-mode):
   - "css":     the page turns are CSS scroll-driven animations. This file only tracks which page is
                current (captions rise, counter "II / V" moves) and handles keyboard / theme editor.
   - "js":      fallback for browsers without scroll timelines. Same values as the CSS, computed here.
   - "stacked": reduced motion (or < 2 pages). Photos one under the other, opacity fade on entry.
   Performance: one passive scroll listener for every album on the page; work runs in one
   requestAnimationFrame per frame, only while an album is near the viewport (IntersectionObserver).
   Each frame reads one rect per album first, then writes; inline styles are written only when they
   change; will-change is set only on the pages that are moving and removed when they stop. */
(() => {
  if (window.TICAlbum) return;
  const albums = new Set();
  window.TICAlbum = { v: 1, albums };

  const mm = (q) => (window.matchMedia ? window.matchMedia(q) : { matches: false, addEventListener() {} });
  const mqReduce = mm('(prefers-reduced-motion: reduce)');
  const mqPhone = mm('(max-width: 749px)');
  const canCSS = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: --a') && CSS.supports('view-timeline: --a block'));
  const design = !!(window.Shopify && window.Shopify.designMode);
  const clamp = (v, lo = 0, hi = 1) => (v < lo ? lo : v > hi ? hi : v);
  // progress 0..1 with float noise snapped to the ends (T - (n - 2 + h) can come out as 0.9999999999999996)
  const prog = (v) => { const c = clamp(v); return c < 1e-4 ? 0 : c > 1 - 1e-4 ? 1 : c; };

  // cubic-bezier(x1, y1, x2, y2) as a function of progress (Newton-Raphson, bisection fallback)
  const bezier = (x1, y1, x2, y2) => {
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const X = (t) => ((ax * t + bx) * t + cx) * t;
    const Y = (t) => ((ay * t + by) * t + cy) * t;
    const dX = (t) => (3 * ax * t + 2 * bx) * t + cx;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let t = x;
      for (let i = 0; i < 8; i++) {
        const e = X(t) - x;
        if (Math.abs(e) < 1e-6) return Y(t);
        const d = dX(t);
        if (Math.abs(d) < 1e-6) break;
        t -= e / d;
      }
      let lo = 0, hi = 1;
      t = x;
      for (let i = 0; i < 40; i++) {
        const v = X(t);
        if (Math.abs(v - x) < 1e-6) break;
        if (v < x) lo = t; else hi = t;
        t = (lo + hi) / 2;
      }
      return Y(t);
    };
  };
  const easeTurn = bezier(0.65, 0, 0.35, 1); // --tic-album-ease (scroll-scrubbed page turn)
  const easeOut = bezier(0.23, 1, 0.32, 1); // --tic-ease-out (settle)

  /* One scroll + one resize listener for all albums; reads first, then writes. */
  let raf = 0;
  const frame = () => {
    raf = 0;
    const live = [];
    albums.forEach((a) => { if (a.live) live.push(a); });
    const rects = live.map((a) => a.read());
    live.forEach((a, i) => a.write(rects[i]));
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(frame); };
  window.addEventListener('scroll', () => {
    for (const a of albums) if (a.live) return schedule();
  }, { passive: true });
  window.addEventListener('resize', () => {
    albums.forEach((a) => a.measure());
    schedule();
  }, { passive: true });
  window.addEventListener('load', schedule);

  class Album {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.tic-album__track');
      this.stage = root.querySelector('.tic-album__stage');
      this.pages = [...root.querySelectorAll('.tic-album__page')];
      this.n = this.pages.length;
      this.h = parseFloat(root.dataset.hold) || 0.35;
      this.T = Math.max(this.n - 1 + this.h, 0.01);
      this.els = this.pages.map((li) => {
        const edge = li.querySelector('.tic-album__edge');
        return {
          sheet: li.querySelector('.tic-album__sheet'),
          media: li.querySelector('.tic-album__media'),
          shade: li.querySelector('.tic-album__shade'),
          edge,
          glow: edge ? edge.firstElementChild : null,
        };
      });
      this.fill = root.querySelector('.tic-album__fill');
      this.cta = root.querySelector('.tic-album__cta');
      this.written = new WeakMap();
      this.cur = -2;
      this.live = false;
      this.onFocus = () => { if (this.cur !== this.n - 1) this.reveal(this.n - 1); };
      // If the inline safety net already fell back to "stacked" (this file arrived late), stay there:
      // switching to a pinned layout now would move the page under the visitor.
      this.setup(root.dataset.mode === 'stacked' && !root.hasAttribute('data-ready'));
    }

    mode(keepStacked) {
      const forced = this.root.getAttribute('data-force'); // preview / QA only
      if (forced) return forced;
      if (keepStacked || this.n < 2 || mqReduce.matches) return 'stacked';
      return canCSS ? 'css' : 'js';
    }

    setup(keepStacked = false) {
      this.teardown();
      const m = (this.m = this.mode(keepStacked));
      this.root.dataset.mode = m;
      this.root.classList.toggle('is-pinned', m !== 'stacked');
      if (m === 'stacked') {
        const io = (this.io = 'IntersectionObserver' in window
          ? new IntersectionObserver((entries) => entries.forEach((e) => {
              if (!e.isIntersecting) return;
              e.target.classList.add('is-seen');
              io.unobserve(e.target);
            }), { rootMargin: '0px 0px -8% 0px', threshold: 0 })
          : null);
        this.pages.forEach((p) => (io && !design ? io.observe(p) : p.classList.add('is-seen')));
      } else {
        this.measure();
        this.io = new IntersectionObserver(([e]) => {
          this.live = e.isIntersecting;
          if (!this.live) this.els.forEach((o) => this.set(o.media, 'willChange', ''));
          schedule();
        }, { rootMargin: '25% 0px 25% 0px' });
        this.io.observe(this.root);
        if (this.cta) this.cta.addEventListener('focus', this.onFocus);
      }
      this.root.setAttribute('data-ready', '');
      schedule();
    }

    teardown() {
      if (this.io) this.io.disconnect();
      this.io = null;
      this.live = false;
      if (this.cta) this.cta.removeEventListener('focus', this.onFocus);
      const clear = (el) => {
        if (!el) return;
        ['clipPath', 'transform', 'opacity', 'willChange'].forEach((p) => { el.style[p] = ''; });
      };
      clear(this.stage);
      clear(this.fill);
      this.els.forEach((o) => [o.sheet, o.media, o.shade, o.edge, o.glow].forEach(clear));
      this.written = new WeakMap();
      this.pages.forEach((p) => p.classList.remove('is-current', 'is-seen'));
      this.cur = -2;
    }

    measure() {
      this.vh = window.innerHeight;
      this.phone = mqPhone.matches;
    }

    read() {
      const r = this.track.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom, height: r.height };
    }

    set(el, prop, val) {
      if (!el) return;
      let w = this.written.get(el);
      if (!w) this.written.set(el, (w = {}));
      if (w[prop] === val) return;
      w[prop] = val;
      el.style[prop] = val;
    }

    setCurrent(cur) {
      this.cur = cur;
      this.pages.forEach((p, i) => p.classList.toggle('is-current', i === cur));
      this.root.style.setProperty('--cur', String(Math.max(0, cur)));
      this.root.dataset.current = String(cur);
      if (this.m !== 'stacked') this.prime(Math.max(0, cur) + 1);
    }

    // Decode the photo of page k before its turn starts. A fully clipped page is never painted, so
    // without this its photo is decoded only when the first sliver of the wipe shows it: on a slow
    // phone that sliver can paint dark for a frame or two. One page ahead only (decoded bitmaps of
    // 2400 px photos are ~15 MB each).
    prime(k) {
      const li = this.pages[k];
      if (!li || li.__ticPrimed) return;
      li.__ticPrimed = true;
      const img = li.querySelector('img');
      if (!img || typeof img.decode !== 'function') return;
      const go = () => { img.decode().catch(() => {}); };
      if (img.complete && img.naturalWidth) go();
      else img.addEventListener('load', go, { once: true });
    }

    write(r) {
      const { vh, T, h, n } = this;
      const pin = Math.max(1, r.height - vh); // = the CSS view timeline's "contain" range
      const u = clamp(-r.top / pin) * T; // 0 .. T, in pages
      // Current page: page k takes over once its caption corner (bottom left) has been uncovered, so
      // the caption never rises half under the outgoing page. Desktop wipe runs right -> left and
      // uncovers that corner last: 85% of the turn (the edge is then ~2% from the left). The phone
      // wipe runs bottom -> top and uncovers it first: 60%. Nothing before the album is in view.
      const at = this.phone ? 0.6 : 0.85;
      let cur = r.top > vh * 0.35 ? -1 : 0;
      for (let k = 1; k < n; k++) if (u >= k - 1 + h + at * (1 - h)) cur = k;
      if (cur !== this.cur) this.setCurrent(cur);
      if (this.m !== 'js') return;

      const ph = this.phone;
      // Album opens from a rounded frame on entry (30% -> 100%) and closes into it on exit (0 -> 60%).
      const entry = (vh - r.top) / vh;
      const open = clamp((entry - 0.3) / 0.7);
      const close = clamp((vh - r.bottom) / vh / 0.6);
      const f = close > 0 ? easeTurn(close) : 1 - easeOut(open);
      this.set(this.stage, 'clipPath', f > 0.0005
        ? `inset(calc(${f.toFixed(4)} * var(--tic-album-fy)) calc(${f.toFixed(4)} * var(--tic-album-fx)) round calc(${f.toFixed(4)} * var(--tic-r-img, 0.8rem)))`
        : '');
      this.set(this.fill, 'transform', `scaleX(${(u / T).toFixed(4)})`);

      for (let k = 0; k < n; k++) {
        const o = this.els[k];
        const t = k ? prog((u - (k - 1 + h)) / (1 - h)) : 1; // turn into page k
        const out = k < n - 1 ? prog((u - (k + h)) / (1 - h)) : 0; // turn of the next page over this one
        // settle: page k from the start of its turn to the end of the pause after it;
        // page 0 from entry 30% to the end of the first pause
        const s = k
          ? prog(u - (k - 1 + h))
          : prog((-r.top + 0.7 * vh) / ((h / T) * pin + 0.7 * vh));
        if (k) {
          const c = ((1 - easeTurn(t)) * 100).toFixed(3);
          this.set(o.sheet, 'clipPath', ph ? `inset(${c}% 0 0 0)` : `inset(0 0 0 ${c}%)`);
          this.set(o.edge, 'transform', ph ? `translate3d(0, ${c}%, 0)` : `translate3d(${c}%, 0, 0)`);
          this.set(o.glow, 'opacity', t <= 0 || t >= 1 ? '0' : Math.min(1, t / 0.08, (1 - t) / 0.12).toFixed(3));
        }
        let tf = '';
        if (out > 0) {
          if (!ph) tf = `translate3d(${(-4 * easeTurn(out)).toFixed(3)}%, 0, 0)`;
        } else {
          const p = 1 - easeOut(s);
          if (p > 0.0005) {
            if (!k) tf = `scale(${(1 + (ph ? 0.05 : 0.08) * p).toFixed(4)})`;
            else tf = ph
              ? `translate3d(0, ${(2 * p).toFixed(3)}%, 0) scale(${(1 + 0.04 * p).toFixed(4)})`
              : `translate3d(${(3 * p).toFixed(3)}%, 0, 0) scale(${(1 + 0.06 * p).toFixed(4)})`;
          }
        }
        this.set(o.media, 'transform', tf);
        this.set(o.shade, 'opacity', out > 0 ? (easeTurn(out) * (ph ? 0.45 : 0.55)).toFixed(3) : '');
        const moving = (k > 0 && t > 0 && t < 1) || (out > 0 && out < 1) || (s > 0 && s < 1);
        this.set(o.media, 'willChange', moving ? 'transform' : '');
      }
    }

    // Scroll so that page k has turned and settled (keyboard focus on the button, theme editor).
    reveal(k) {
      if (this.m === 'stacked') {
        this.pages[k].scrollIntoView({ block: 'start' });
        return;
      }
      const r = this.track.getBoundingClientRect();
      const pin = Math.max(1, r.height - window.innerHeight);
      const u = Math.min(this.T, k + this.h * 0.5);
      const y = Math.round(window.scrollY + r.top + (u / this.T) * pin);
      try { window.scrollTo({ top: y, behavior: 'instant' }); } catch (e) { window.scrollTo(0, y); }
      schedule();
    }
  }

  const init = (el) => {
    if (el.__ticAlbum) return;
    const a = new Album(el);
    el.__ticAlbum = a;
    albums.add(a);
  };
  const boot = (scope) => scope.querySelectorAll('.tic-album').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);

  // reduced-motion preference changed while the page is open: rebuild
  const onPref = () => albums.forEach((a) => a.setup());
  if (mqReduce.addEventListener) mqReduce.addEventListener('change', onPref);
  else if (mqReduce.addListener) mqReduce.addListener(onPref);

  // Theme editor
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('.tic-album').forEach((el) => {
      const a = el.__ticAlbum;
      if (!a) return;
      a.teardown();
      albums.delete(a);
      el.__ticAlbum = null;
    });
  });
  document.addEventListener('shopify:block:select', (e) => {
    const li = e.target && e.target.closest ? e.target.closest('.tic-album__page') : null;
    const a = li && li.closest('.tic-album') && li.closest('.tic-album').__ticAlbum;
    if (a) a.reveal(a.pages.indexOf(li));
  });
})();
